import { useRef, useState } from 'react';
import { useLocale } from '../context/LocaleContext';
import { FinalCTA } from '../components/shared/PageSections';
import { CheckIcon } from '../components/ui/Icons';
import { ButtonLink, Eyebrow } from '../components/ui/Primitives';

function PricingCard({ plan, count, content, index, locale }) {
  const numericPrice = Number(plan.price);
  const price = Number.isFinite(numericPrice) ? new Intl.NumberFormat(`${locale}-CH`).format(numericPrice * count) : plan.price;
  const isDisabled = plan.ctaState.gated
    ? count < plan.ctaState.minimumPositions
    : plan.ctaState.disabled;
  return (
    <article className="pricing-card" data-plan={plan.name.toLowerCase()} style={{ '--stagger': index }}>
      <div className="pricing-card__head">
        <span>{plan.name}</span>
        {index === 2 ? <em>Pro</em> : null}
      </div>
      <div className="pricing-card__price">
        <strong>{price}</strong>
        {Number.isFinite(numericPrice) ? <span>{content.currency}<br />{content.period}</span> : null}
      </div>
      <p>{plan.description}</p>
      <hr />
      <small>{content.includedLabel}</small>
      <ul>{plan.features.map((feature) => (
        <li key={feature.label} data-available={feature.available || undefined}>
          <i>{feature.available ? <CheckIcon /> : <span>×</span>}</i><span>{feature.label}</span>
        </li>
      ))}</ul>
      <ButtonLink
        disabled={isDisabled}
        to={plan.ctaState.gated ? 'mailto:info@yourapplicant.ch' : undefined}
        variant={index === 2 ? 'orange' : 'outline'}
      >{plan.cta}</ButtonLink>
      {plan.note && isDisabled ? <p className="pricing-card__note">{plan.note}</p> : null}
    </article>
  );
}

export default function PricingPage() {
  const { t, locale } = useLocale();
  const pricing = t.pricing;
  const [audience, setAudience] = useState('companies');
  const [count, setCount] = useState(1);
  const [studentCount, setStudentCount] = useState(1);
  const audienceKeys = Object.keys(pricing.hero.tabs);
  const tabRefs = useRef([]);

  const selectAudience = (index) => {
    const nextIndex = (index + audienceKeys.length) % audienceKeys.length;
    setAudience(audienceKeys[nextIndex]);
    window.requestAnimationFrame(() => tabRefs.current[nextIndex]?.focus());
  };

  const onTabKeyDown = (event, index) => {
    const keyActions = {
      ArrowRight: () => selectAudience(index + 1),
      ArrowDown: () => selectAudience(index + 1),
      ArrowLeft: () => selectAudience(index - 1),
      ArrowUp: () => selectAudience(index - 1),
      Home: () => selectAudience(0),
      End: () => selectAudience(audienceKeys.length - 1),
    };
    if (!keyActions[event.key]) return;
    event.preventDefault();
    keyActions[event.key]();
  };

  return (
    <main id="main-content" className="pricing-page">
      <section className="pricing-hero" aria-labelledby="pricing-title">
        <Eyebrow>{pricing.hero.eyebrow}</Eyebrow>
        <div className="pricing-tabs" role="tablist" data-selected={audience}>
          {Object.entries(pricing.hero.tabs).map(([key, label], index) => (
            <button
              key={key}
              id={`pricing-tab-${key}`}
              ref={(node) => { tabRefs.current[index] = node; }}
              type="button"
              role="tab"
              aria-selected={audience === key}
              aria-controls="pricing-audience-panel"
              tabIndex={audience === key ? 0 : -1}
              onClick={() => setAudience(key)}
              onKeyDown={(event) => onTabKeyDown(event, index)}
            >{label}</button>
          ))}
          <i aria-hidden="true" />
        </div>
        <h1 id="pricing-title">{pricing.hero.title}</h1>
      </section>

      {audience === 'companies' ? (
        <section id="pricing-audience-panel" className="pricing-content" role="tabpanel" aria-labelledby="pricing-tab-companies">
          <div className="pricing-counter" data-reveal="fade">
            <span>{pricing.counterLabel}</span>
            <div>
              <button type="button" aria-label={t.common.aria.decreasePositions} onClick={() => setCount((value) => Math.max(1, value - 1))}>−</button>
              <strong key={count}>{count}</strong>
              <button type="button" aria-label={t.common.aria.increasePositions} onClick={() => setCount((value) => Math.min(50, value + 1))}>+</button>
            </div>
          </div>
          <div className="pricing-grid" data-reveal="stagger">
            {pricing.plans.map((plan, index) => <PricingCard key={plan.name} plan={plan} count={count} content={pricing} index={index} locale={locale} />)}
          </div>
        </section>
      ) : (
        <section id="pricing-audience-panel" className="school-pricing-panel" role="tabpanel" aria-labelledby="pricing-tab-schools" key="schools">
          <div className="school-pricing-panel__orb" aria-hidden="true" />
          <div className="school-pricing-panel__copy">
            <Eyebrow tone="light">{pricing.schoolsPanel.eyebrow}</Eyebrow>
            <h2>{pricing.schoolsPanel.title} <span>{pricing.schoolsPanel.titleAccent}</span></h2>
            <p>{pricing.schoolsPanel.description}</p>
          </div>
          <article className="school-pricing-card glass-card">
            <em>{pricing.schoolsPanel.trialBadge}</em>
            <div className="school-pricing-card__counter">
              <span>{pricing.schoolsPanel.studentCountLabel}</span>
              <div>
                <button type="button" aria-label={`${pricing.schoolsPanel.studentCountLabel}: −`} onClick={() => setStudentCount((value) => Math.max(1, value - 1))}>−</button>
                <strong key={studentCount}>{studentCount}</strong>
                <button type="button" aria-label={`${pricing.schoolsPanel.studentCountLabel}: +`} onClick={() => setStudentCount((value) => Math.min(1000, value + 1))}>+</button>
              </div>
            </div>
            <div className="school-pricing-card__price">
              <strong>{new Intl.NumberFormat(`${locale}-CH`).format(Number(pricing.schoolsPanel.price) * studentCount)}</strong>
              <span>{pricing.schoolsPanel.currency}<br />{pricing.schoolsPanel.period}</span>
            </div>
            <small>{pricing.schoolsPanel.price} {pricing.schoolsPanel.currency} {pricing.schoolsPanel.perStudentLabel}</small>
            <ul>{pricing.schoolsPanel.features.map((feature) => (
              <li key={feature.label} data-available={feature.available || undefined}>
                <i>{feature.available ? <CheckIcon /> : <span>×</span>}</i><span>{feature.label}</span>
              </li>
            ))}</ul>
            <ButtonLink variant="light" to="https://calendly.com/your-applicant/schulen" target="_blank" rel="noopener noreferrer">{pricing.schoolsPanel.cta}</ButtonLink>
            <p>{pricing.schoolsPanel.note}</p>
          </article>
        </section>
      )}

      <FinalCTA eyebrow={t.home.closing.eyebrow} title={t.home.closing.title} text={t.home.closing.description} buttonLabel={t.home.closing.cta} />
    </main>
  );
}
